import { useEffect, useState } from "react";
import { cn } from "@/lib/cn";
import { useAgentTextMotion } from "./use-agent-text-motion";
import "./agent-status-surface.css";
import "./agent-text-motion.css";

const STATUS_SWAP_WINDOW_MS = 200;

type StatusTextView = {
  text: string;
  previous: string | null;
  revision: number;
};

/** Transitions.dev Thinking states / Text states swap, driven by real updates.
 * The latest label sizes the surface immediately; only text moves inside it. */
export function AgentStatusText({
  text,
  working = false,
  className,
  "data-testid": testId,
}: {
  text: string;
  working?: boolean;
  className?: string;
  "data-testid"?: string;
}) {
  const motion = useAgentTextMotion();
  const [view, setView] = useState<StatusTextView>({
    text,
    previous: null,
    revision: 0,
  });
  if (view.text !== text) {
    setView({
      text,
      previous: motion ? view.text : null,
      revision: view.revision + 1,
    });
  } else if (!motion && view.previous !== null) {
    setView({ ...view, previous: null });
  }

  useEffect(() => {
    if (view.previous === null) return;
    const timer = window.setTimeout(() => {
      setView((current) => ({ ...current, previous: null }));
    }, STATUS_SWAP_WINDOW_MS);
    return () => window.clearTimeout(timer);
  }, [view.previous, view.revision]);

  return (
    <span
      className={cn("denker-status-text", className)}
      data-testid={testId}
      data-text-motion={motion ? "enabled" : "disabled"}
    >
      {view.previous !== null ? (
        <span
          key={`exit:${view.revision}`}
          className="denker-status-text-exit"
          aria-hidden="true"
        >
          {view.previous}
        </span>
      ) : null}
      <span
        key={view.revision}
        className={cn(
          "denker-status-text-current",
          view.previous !== null && "denker-status-text-enter",
        )}
      >
        <span className={cn(working && "denker-agent-status-shimmer")}>
          {text}
        </span>
      </span>
    </span>
  );
}
